import { useEffect, useRef, useState } from 'react'
import { router } from '@inertiajs/react'
import { getCableConsumer } from '@/lib/cable'
import { KonexzeroPeer, type KonexzeroConnectionState } from '@/lib/konexzero-peer'

export type CallKind = 'dm_1on1' | 'group_audio' | 'group_video' | 'voice_room' | 'live_stream' | 'gravity'

export type CallEvent =
  | { type: 'started' }
  | { type: 'ended' }
  | { type: 'participant_joined'; user_id: string; kz_participant_id?: string; role: string }
  | { type: 'participant_published'; user_id: string; kz_participant_id?: string; track_names: string[] }
  | { type: 'participant_left'; user_id: string; kz_participant_id?: string }
  | { type: 'hand_raised'; user_id: string; raised: boolean }
  | { type: 'speaker_promoted'; user_id: string }
  | { type: 'speaker_demoted'; user_id: string }
  | { type: 'muted_by_host'; user_id: string }
  | { type: 'media_toggled'; user_id: string; kind: 'audio' | 'video'; enabled: boolean }
  | { type: 'live_join_request'; user_id: string; handle: string; display_name: string; avatar_url: string | null }

type ChannelHandle = { perform: (action: string, data?: Record<string, unknown>) => void; unsubscribe: () => void }

export type CallState = 'idle' | 'connecting' | 'getting_media' | 'publishing' | 'live' | 'ended' | 'failed'

export type RemotePeer = {
  participantId: string
  userId?: string
  stream: MediaStream
  audioOn: boolean
  videoOn: boolean
}

export interface UseCallOptions {
  kind: CallKind
  myUserId: string
  myDisplayName: string
  isHost: boolean
  onEvent?: (e: CallEvent) => void
}

export interface UseCallResult {
  state: CallState
  error: string | null
  // Categorized media-acquisition error so the UI can show actionable text
  // ("Allow camera & mic", "No microphone found") without parsing strings.
  mediaError: 'permission_denied' | 'no_device' | 'in_use' | 'unsupported' | 'other' | null
  // Underlying RTC connection state. Used to render a "Reconnecting…"
  // banner during ICE restarts and a "Connection lost" hint after the
  // restart cap is exhausted.
  connectionState: KonexzeroConnectionState
  localStream: MediaStream | null
  remotePeers: RemotePeer[]
  micEnabled: boolean
  camEnabled: boolean
  toggleMic: () => void
  toggleCam: () => void
  end: () => Promise<void>
  perform: (action: string, data?: Record<string, unknown>) => void
}

const PROXY_BASE = '/konexzero/api/v1'

function mediaForKind(kind: CallKind, isHost: boolean): { audio: boolean; video: boolean } {
  switch (kind) {
    case 'voice_room':
    case 'group_audio':
    case 'dm_1on1':
      // The Phone icon in DM uses dm_1on1 — audio-only by design.
      // The Video icon switches to group_video which carries cam too.
      return { audio: true, video: false }
    case 'live_stream':
      // Hosts publish audio+video; viewers don't publish at all.
      return isHost ? { audio: true, video: true } : { audio: false, video: false }
    default:
      return { audio: true, video: true }
  }
}

function roomTypeForKind(kind: CallKind): 'group' | '1-to-1' {
  return kind === 'dm_1on1' ? '1-to-1' : 'group'
}

// Map stgbee's track names to media kinds so KonexzeroPeer can use the
// single-round-trip subscribe (it pre-builds recvonly transceivers, which
// requires knowing each track's kind up front). stgbee publishes only
// 'mic' and 'cam' today; any unknown name returns undefined, dropping
// that subscription back to the legacy two-step flow.
function kindsForTrackNames(names: string[]): ('audio' | 'video')[] | undefined {
  const kinds: ('audio' | 'video')[] = []
  for (const n of names) {
    if (/mic|audio/i.test(n)) kinds.push('audio')
    else if (/cam|video|screen/i.test(n)) kinds.push('video')
    else return undefined
  }
  return kinds
}

// Categorize a getUserMedia error into a stable code the UI can branch on.
function classifyMediaError(err: unknown): UseCallResult['mediaError'] {
  if (typeof DOMException !== 'undefined' && err instanceof DOMException) {
    switch (err.name) {
      case 'NotAllowedError':
      case 'SecurityError':
        return 'permission_denied'
      case 'NotFoundError':
      case 'OverconstrainedError':
        return 'no_device'
      case 'NotReadableError':
      case 'TrackStartError':
      case 'AbortError':
        return 'in_use'
      case 'TypeError':
        return 'unsupported'
    }
  }
  return 'other'
}

export function useCall(roomKey: string | null, opts: UseCallOptions | null): UseCallResult {
  const [state, setState] = useState<CallState>('idle')
  const [error, setError] = useState<string | null>(null)
  const [mediaError, setMediaError] = useState<UseCallResult['mediaError']>(null)
  const [connectionState, setConnectionState] = useState<KonexzeroConnectionState>('new')
  const [localStream, setLocalStream] = useState<MediaStream | null>(null)
  const [remotePeers, setRemotePeers] = useState<RemotePeer[]>([])
  const [micEnabled, setMicEnabled] = useState(true)
  const [camEnabled, setCamEnabled] = useState(true)

  const peerRef = useRef<KonexzeroPeer | null>(null)
  const channelRef = useRef<ChannelHandle | null>(null)
  // Catch-all cleanup hook for things spawned mid-effect (e.g., the
  // periodic quality reporter interval). The effect's outer cleanup
  // calls this on unmount or roomKey change.
  const cleanupRef = useRef<(() => void) | null>(null)
  // Track names we've successfully published — used to re-broadcast
  // participant_published when a new peer joins (so they can subscribe
  // to us without waiting for a sweep cycle).
  const publishedTrackNamesRef = useRef<string[]>([])
  // Peers we've already swept. Stored on a ref so the cable handler can
  // delete entries when a peer leaves — otherwise a leave+rejoin gets
  // skipped by both the sweep dedup AND KonexzeroPeer's subscribe guard.
  const sweptPeerIdsRef = useRef<Set<string>>(new Set())
  // Most recent sweep function — invoked from the cable subscription's
  // `connected` callback so a websocket reconnect re-pulls the room
  // state and resubscribes to anyone who joined during the disconnect.
  const sweepRef = useRef<((label: string) => Promise<void>) | null>(null)
  // Peers we've actually received an `onRemoteStream` callback for —
  // proves that ontrack routed media to a tile, not just that /subscribe
  // returned 200. The watchdog (subscribeWithWatchdog) checks this set
  // after each subscribe; if missing 8s later, force eviction + sweep.
  // Cleared on participant_left and on cable reconnect so a rejoining
  // peer gets a fresh watchdog cycle.
  const streamArrivedRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    if (!roomKey || !opts) {
      // No active call — reset state and bail
      setState('idle')
      setError(null)
      setMediaError(null)
      setConnectionState('new')
      setLocalStream(null)
      setRemotePeers([])
      return
    }
    let cancelled = false

    const media = mediaForKind(opts.kind, opts.isHost)
    // Tab-scoped session-id cache. KonexZero returns the SFU session_id only
    // to the joining participant; persisting it across mounts lets a hard
    // refresh or transient remount resume the same upstream session instead
    // of looking like a leave+rejoin to other peers.
    const sessionStorageKey = `kz-session:${roomKey}`
    const resumeSessionId = typeof window !== 'undefined'
      ? window.sessionStorage.getItem(sessionStorageKey) ?? undefined
      : undefined
    const peer = new KonexzeroPeer({
      basePath: PROXY_BASE,
      roomKey,
      participantId: `user_${opts.myUserId}`,
      displayName: opts.myDisplayName,
      roomType: roomTypeForKind(opts.kind),
      audio: media.audio,
      video: media.video,
      resumeSessionId,
      onLog: (level, text) => {
        if (level === 'error') {
          console.warn('[konex]', text)
        } else if (import.meta.env.DEV) {
          // Surface request/response/info traces during development so
          // ontrack routing decisions, subscribe retries, resume hints
          // and the per-API-call trace are visible in DevTools. Keep this
          // gated on DEV so prod consoles stay quiet.
          console.info(`[konex/${level}]`, text)
        }
      },
      onRemoteStream: (peerId, stream) => {
        if (cancelled) return
        // Watchdog: a stream arrived for this peer. Clears the timer
        // scheduled by subscribeWithWatchdog so we don't wrongly evict.
        streamArrivedRef.current.add(peerId)
        setRemotePeers((prev) => {
          if (prev.some((p) => p.participantId === peerId)) return prev
          const userId = peerId.startsWith('user_') ? peerId.slice(5) : undefined
          return [...prev, { participantId: peerId, userId, stream, audioOn: true, videoOn: true }]
        })
      },
      onConnectionStateChange: (s) => {
        if (cancelled) return
        if (import.meta.env.DEV) console.info('[konex] connectionState →', s)
        setConnectionState(s)
      }
    })
    peerRef.current = peer

    ;(async () => {
      try {
        const csrf = (document.querySelector('meta[name=csrf-token]') as HTMLMetaElement | null)?.content ?? ''

        // 1) Token endpoint — runs the server-side join against KonexZero so
        //    the room exists upstream. Idempotent; doesn't broadcast.
        const tokenRes = await fetch(`/calls/${roomKey}/tokens`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf, 'X-Requested-With': 'XMLHttpRequest' },
          credentials: 'same-origin'
        })
        if (!tokenRes.ok) throw new Error(`token ${tokenRes.status}`)

        if (cancelled) return
        setState('connecting')

        // Helper: subscribe to one or more peers + arm a watchdog that
        // detects "/subscribe returned 200 but ontrack never fired".
        // This is the production-grade backstop for SFU half-success
        // states. Each peer is wrapped individually so one peer's
        // watchdog firing doesn't affect the others. Defined here so
        // the cable handler below (set up before peer.joinRoom returns)
        // can reference it; the closure captures by-reference and the
        // function body is only invoked from inside event callbacks.
        const STREAM_WATCHDOG_MS = 8_000
        const subscribeWithWatchdog = async (
          subs: { participant_id: string; track_names: string[]; track_kinds?: ('audio' | 'video')[] }[],
          source: string
        ) => {
          for (const sub of subs) {
            const peerId = sub.participant_id
            try {
              await peerRef.current?.subscribeTo([sub])
              if (cancelled) return
              // Arm the watchdog. If onRemoteStream hasn't fired for
              // this peer within STREAM_WATCHDOG_MS, treat the subscribe
              // as a half-success and force a fresh attempt. Most often
              // a CF Calls quirk where the SDP exchange completes but
              // the SFU didn't actually wire forwarding.
              window.setTimeout(() => {
                if (cancelled) return
                if (streamArrivedRef.current.has(peerId)) return
                console.warn(`[useCall] subscribe(${source}) to ${peerId} returned ok but no ontrack within ${STREAM_WATCHDOG_MS}ms — evicting + re-sweeping`)
                peerRef.current?.forgetPeer(peerId)
                sweptPeerIdsRef.current.delete(peerId)
                sweepRef.current?.(`watchdog-${peerId}`).catch(() => {})
              }, STREAM_WATCHDOG_MS)
            } catch (err) {
              // Failure path: evict from sweep dedup so retries can
              // re-attempt cleanly, schedule a couple of explicit
              // follow-ups in case the periodic sweep cadence is too
              // slow.
              console.warn(`[useCall] subscribe(${source}) to ${peerId} failed:`, err)
              sweptPeerIdsRef.current.delete(peerId)
              window.setTimeout(() => sweepRef.current?.(`retry-5s-${peerId}`).catch(() => {}), 5000)
              window.setTimeout(() => sweepRef.current?.(`retry-20s-${peerId}`).catch(() => {}), 20_000)
            }
          }
        }

        // 2) Subscribe to CallChannel BEFORE calling /join so we don't miss
        //    the host's re-broadcast of `participant_published`. The server
        //    broadcasts `participant_joined` when /join lands and the host's
        //    useCall immediately re-publishes — that broadcast must arrive
        //    AFTER our subscription is active, otherwise the second user
        //    never gets cable-event notification of the host's tracks and
        //    has to wait for the sweep cycle to discover them.
        const consumer = getCableConsumer()
        let firstConnect = true
        const sub = consumer.subscriptions.create(
          { channel: 'CallChannel', room_key: roomKey },
          {
            connected: () => {
              // First `connected` is the initial subscribe. Subsequent
              // ones mean ActionCable dropped and reconnected — events
              // during the gap are lost, so re-sweep the room to pick
              // up any peer that joined or republished while we were
              // offline. Clear the sweep dedup so even peers we'd
              // previously swept get re-evaluated; their tracks may
              // have changed (publish-after-rejoin) during the gap.
              if (firstConnect) { firstConnect = false; return }
              console.info('[useCall] cable reconnected — clearing sweep + watchdog state, running recovery sweep')
              sweptPeerIdsRef.current.clear()
              streamArrivedRef.current.clear()
              sweepRef.current?.('cable-reconnect').catch(() => {})
            },
            received: (data: CallEvent) => {
              opts.onEvent?.(data)
              if (data.type === 'participant_published' && data.user_id !== opts.myUserId) {
                // The publisher already has tracks ready — subscribe now
                // without racing on track existence. The SDK now does
                // deterministic ontrack routing via SDP a=msid parsing
                // and retries ~23s across 5 attempts on the propagation
                // race. After subscribeTo returns, the watchdog covers
                // the case where /subscribe returned 200 but ontrack
                // never fires (rare but happens when the SFU accepts
                // the offer without actually wiring forwarding).
                const remoteId = data.kz_participant_id || `user_${data.user_id}`
                void subscribeWithWatchdog([{
                  participant_id: remoteId,
                  track_names: data.track_names,
                  track_kinds: kindsForTrackNames(data.track_names)
                }], 'cable-published')
              } else if (data.type === 'participant_joined' && data.user_id !== opts.myUserId) {
                // A new peer just arrived. If I'm already publishing,
                // re-broadcast my published event so they can subscribe
                // to me without waiting for the next sweep cycle. This
                // is the rejoin-recovery path: the new peer missed my
                // original participant_published broadcast.
                if (publishedTrackNamesRef.current.length > 0) {
                  const csrf = (document.querySelector('meta[name=csrf-token]') as HTMLMetaElement | null)?.content ?? ''
                  fetch(`/calls/${roomKey}/published`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf, 'X-Requested-With': 'XMLHttpRequest' },
                    credentials: 'same-origin',
                    body: JSON.stringify({ track_names: publishedTrackNamesRef.current })
                  }).catch((err) => console.warn('[useCall] re-broadcast on join failed', err))
                }
              } else if (data.type === 'participant_left') {
                const remoteId = data.kz_participant_id || `user_${data.user_id}`
                // Drop the tile and let any phantom variants clear too:
                // we don't always know which stream id KZ used, so also
                // drop tiles whose userId matches.
                setRemotePeers((prev) => prev.filter((p) => p.participantId !== remoteId && p.userId !== data.user_id))
                // Forget so a later rejoin re-subscribes cleanly. Without
                // this both the SDK's subscribedPeerIds and the page's
                // sweep dedup short-circuit on rejoin → no media flows.
                peerRef.current?.forgetPeer(remoteId)
                sweptPeerIdsRef.current.delete(remoteId)
                streamArrivedRef.current.delete(remoteId)
              } else if (data.type === 'media_toggled' && data.user_id !== opts.myUserId) {
                setRemotePeers((prev) => prev.map((p) => {
                  if (p.userId !== data.user_id) return p
                  return data.kind === 'video'
                    ? { ...p, videoOn: data.enabled }
                    : { ...p, audioOn: data.enabled }
                }))
              } else if (data.type === 'live_join_request') {
                // Surface to the page via window event — only the host
                // should react, but the broadcast goes to everyone.
                window.dispatchEvent(new CustomEvent('live:join_request', {
                  detail: { user_id: data.user_id, handle: data.handle, display_name: data.display_name, avatar_url: data.avatar_url }
                }))
              } else if (data.type === 'speaker_promoted' && data.user_id === opts.myUserId) {
                // I just got promoted from viewer to cohost. Acquire
                // media and publish so other peers can subscribe to me.
                console.info('[useCall] promoted to cohost — requesting media…')
                ;(async () => {
                  try {
                    const peer = peerRef.current
                    if (!peer) throw new Error('no active peer')
                    peer.enableMedia(true, true)
                    peer.resetPublishedFlag()
                    let stream: MediaStream | null
                    try {
                      stream = await peer.getUserMedia()
                    } catch (mediaErr) {
                      setMediaError(classifyMediaError(mediaErr))
                      throw mediaErr
                    }
                    setLocalStream(stream)
                    setMicEnabled(true)
                    setCamEnabled(true)
                    // Full SFU rejoin before publishing. Reusing the
                    // viewer-mode PC fails two ways: Chrome's session
                    // validator trips on stale recvonly transceivers
                    // (ERROR_CONTENT on createOffer), and KonexZero
                    // 502s on /publish because Cloudflare Calls binds
                    // our DTLS fingerprint to the existing session.
                    // /leave + /join releases the SFU session and gives
                    // us fresh ICE servers; the new PC then publishes
                    // cleanly.
                    //
                    // We deliberately do NOT clear `setRemotePeers([])`
                    // here anymore — the rebuild invalidates the old
                    // MediaStream objects anyway, and keeping the tiles
                    // visible avoids the "everyone vanishes for a beat"
                    // glitch. Fresh `ontrack` events from the new
                    // subscriptions replace the stale tracks in place.
                    sweptPeerIdsRef.current.clear()
                    await peer.rejoinAsPublisher()
                    await peer.publish()
                    publishedTrackNamesRef.current = [ 'mic', 'cam' ]
                    const csrf = (document.querySelector('meta[name=csrf-token]') as HTMLMetaElement | null)?.content ?? ''
                    await fetch(`/calls/${roomKey}/published`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf, 'X-Requested-With': 'XMLHttpRequest' },
                      credentials: 'same-origin',
                      body: JSON.stringify({ track_names: [ 'mic', 'cam' ] })
                    })
                    console.info('[useCall] promotion complete — published mic + cam')
                    // The PC rebuild dropped our subscriptions; re-run
                    // the recovery sweep to re-attach the other peers'
                    // streams. We retry on a backoff schedule because
                    // Cloudflare's edge takes a few seconds to propagate
                    // the new join — the first sweep at +0s often sees
                    // an empty participant list. Each sweep is deduped
                    // server-side, so the retries are cheap.
                    sweepRef.current?.('post-promotion-rebuild').catch(() => {})
                    window.setTimeout(() => sweepRef.current?.('post-promotion-retry-3s').catch(() => {}), 3000)
                    window.setTimeout(() => sweepRef.current?.('post-promotion-retry-10s').catch(() => {}), 10_000)
                    window.setTimeout(() => sweepRef.current?.('post-promotion-retry-30s').catch(() => {}), 30_000)
                    // Only reload if we're actually on the call page; the
                    // user may have minimized and navigated elsewhere.
                    // `my_role` is the Rails-side prop Live/Stage uses to
                    // decide whether to show the local video tile and the
                    // mic/cam toolbar — it MUST refresh too, otherwise the
                    // freshly-promoted user is stuck rendering as a viewer.
                    if (typeof window !== 'undefined' && window.location.pathname === `/calls/${roomKey}`) {
                      router.reload({ only: ['call', 'my_role'] })
                    }
                  } catch (err) {
                    const msg = err instanceof Error ? err.message : 'unknown error'
                    console.warn('[useCall] promotion publish failed:', msg)
                    // Surface to the page so the user sees something
                    // happened (most common: mic/cam permission denied).
                    window.dispatchEvent(new CustomEvent('live:promotion_failed', { detail: { message: msg } }))
                  }
                })()
              } else if (data.type === 'speaker_demoted' && data.user_id === opts.myUserId) {
                // I just got demoted — either the host kicked me from
                // stage or I stepped down voluntarily. Release the SFU
                // publish session, stop my local tracks (camera light
                // off), and reattach as a viewer. Re-sweep so other
                // broadcasters' streams come back as remote tiles.
                console.info('[useCall] demoted from cohost — stepping down')
                ;(async () => {
                  try {
                    const peer = peerRef.current
                    if (!peer) return
                    setLocalStream(null)
                    setMicEnabled(false)
                    setCamEnabled(false)
                    setRemotePeers([])
                    sweptPeerIdsRef.current.clear()
                    publishedTrackNamesRef.current = []
                    await peer.rejoinAsViewer()
                    sweepRef.current?.('post-demotion-rebuild').catch(() => {})
                    if (typeof window !== 'undefined' && window.location.pathname === `/calls/${roomKey}`) {
                      router.reload({ only: ['call', 'my_role'] })
                    }
                  } catch (err) {
                    const msg = err instanceof Error ? err.message : 'unknown error'
                    console.warn('[useCall] step-down failed:', msg)
                  }
                })()
              } else if (data.type === 'ended') {
                setState('ended')
              }
            }
          }
        )
        channelRef.current = sub as unknown as ChannelHandle

        // 3) Now tell our own server we're entering the call — broadcasts
        //    `participant_joined` to other peers (their useCall handler
        //    then re-broadcasts `/published` so we discover them). This
        //    must happen AFTER step 2's cable subscribe.
        await fetch(`/calls/${roomKey}/join`, {
          method: 'POST',
          headers: { 'X-CSRF-Token': csrf, 'X-Requested-With': 'XMLHttpRequest' },
          credentials: 'same-origin'
        })

        // 4) KonexZero join (browser-side, through our proxy)
        await peer.joinRoom()
        if (cancelled) return
        // Cache the SFU session id for resume on a future mount in this tab.
        const sid = peer.getSessionId()
        if (sid && typeof window !== 'undefined') {
          window.sessionStorage.setItem(sessionStorageKey, sid)
        }

        // 5) Get local mic/cam (skip for live-stream viewers)
        const willPublish = media.audio || media.video
        const publishedTrackNames: string[] = []
        if (willPublish) {
          setState('getting_media')
          let stream: MediaStream | null
          try {
            stream = await peer.getUserMedia()
          } catch (mediaErr) {
            // AbortError fires when getUserMedia is interrupted (rapid
            // mount/unmount, permission prompt race). NotReadableError
            // fires when the camera/mic is briefly held by another tab.
            // Our SDK also surfaces a "timed out" error if the browser
            // hung past 15s. All three are typically transient — one
            // retry usually succeeds. Permission denials propagate.
            const isAbort = mediaErr instanceof DOMException && mediaErr.name === 'AbortError'
            const isNotReadable = mediaErr instanceof DOMException && mediaErr.name === 'NotReadableError'
            const isTimeout = mediaErr instanceof Error && /getUserMedia timed out/.test(mediaErr.message)
            const isStreamInvalid = mediaErr instanceof Error && /not live|no tracks/.test(mediaErr.message)
            const transient = isAbort || isNotReadable || isTimeout || isStreamInvalid
            if (transient && !cancelled) {
              const tag = isAbort ? 'AbortError' : isNotReadable ? 'NotReadableError' : isTimeout ? 'timeout' : 'invalid-stream'
              console.info(`[useCall] getUserMedia ${tag} — retrying once after 600ms`)
              await new Promise((r) => setTimeout(r, 600))
              try {
                stream = await peer.getUserMedia()
              } catch (retryErr) {
                setMediaError(classifyMediaError(retryErr))
                throw retryErr
              }
            } else {
              setMediaError(classifyMediaError(mediaErr))
              throw mediaErr
            }
          }
          if (cancelled) return
          setLocalStream(stream)
          setState('publishing')
          await peer.publish()
          if (media.audio) publishedTrackNames.push('mic')
          if (media.video) publishedTrackNames.push('cam')
          publishedTrackNamesRef.current = publishedTrackNames

          // Tell the server we have tracks ready — it broadcasts
          // participant_published over CallChannel so the others can
          // subscribe to us without racing on track existence. Retry on
          // transient errors so a silently-dropped broadcast doesn't
          // leave subscribers stuck waiting on a sweep cycle.
          const broadcastPublished = async () => {
            const broadcastBackoff = [ 0, 500, 1500, 4000 ]
            for (let attempt = 0; attempt < broadcastBackoff.length; attempt++) {
              if (cancelled) return
              if (broadcastBackoff[attempt] > 0) await new Promise((r) => setTimeout(r, broadcastBackoff[attempt]))
              try {
                const res = await fetch(`/calls/${roomKey}/published`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf, 'X-Requested-With': 'XMLHttpRequest' },
                  credentials: 'same-origin',
                  body: JSON.stringify({ track_names: publishedTrackNames })
                })
                if (res.ok) return
                console.warn(`[useCall] /published returned ${res.status}, retrying`)
              } catch (err) {
                console.warn('[useCall] /published broadcast failed, retrying:', err)
              }
            }
            console.warn('[useCall] /published broadcast exhausted retries — peers will discover via sweep')
          }
          void broadcastPublished()
        }

        if (cancelled) return
        setState('live')

        // Periodically report connection quality. The server endpoint
        // /calls/:key/quality already exists (CallsController#report_quality)
        // and stores into CallParticipant#connection_quality so we can
        // diff RTT/loss across users without standing up a metrics
        // pipeline. 30s cadence keeps the row count modest.
        const qualityInterval = window.setInterval(async () => {
          if (cancelled) return
          try {
            const rtt = await peer.getRtt()
            if (rtt == null) return
            await fetch(`/calls/${roomKey}/quality`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf, 'X-Requested-With': 'XMLHttpRequest' },
              credentials: 'same-origin',
              body: JSON.stringify({ rtt_ms: rtt })
            }).catch(() => {})
          } catch { /* ignore */ }
        }, 30_000)

        // Make sure the interval is cleared when the effect tears down.
        cleanupRef.current = () => window.clearInterval(qualityInterval)

        // 5) Sweep — discover peers we missed (e.g., participant_published
        //    fired before our CallChannel subscription was active). The
        //    sweep is the *self-healing* path for the whole subscribe
        //    graph: cable events are best-effort, KZ edge propagation is
        //    not instant, and ICE handshakes can flap.
        //
        // Critical invariant: a peer is added to `sweptPeerIdsRef` ONLY
        // after `subscribeTo` returns successfully. If we marked them up
        // front and the subscribe failed (e.g. tracks_not_ready race),
        // every subsequent sweep would skip the peer because of the
        // dedup — they'd be permanently invisible until something else
        // (cable reconnect, participant_left, page reload) cleared the
        // ref. Mark-after-success guarantees the next sweep retries.
        const runSweep = async (label: string) => {
          if (cancelled) return
          try {
            const partRes = await fetch(`${PROXY_BASE}/rooms/${roomKey}`, {
              method: 'GET',
              headers: { 'X-Participant-Id': `user_${opts.myUserId}`, Accept: 'application/json' },
              credentials: 'same-origin'
            })
            if (!partRes.ok) return
            const data = await partRes.json()
            const participants: Record<string, { participant_id: string; tracks?: Record<string, { kind?: string }> }> = data?.data?.participants ?? {}
            const candidates: { participant_id: string; track_names: string[]; track_kinds?: ('audio' | 'video')[] }[] = []
            for (const [pid, p] of Object.entries(participants)) {
              if (pid === `user_${opts.myUserId}`) continue
              if (sweptPeerIdsRef.current.has(pid)) continue
              const trackEntries = Object.entries(p.tracks ?? {})
              if (trackEntries.length === 0) continue
              const names = trackEntries.map(([name]) => name)
              // KZ's participant record carries each track's kind — use it
              // directly so the SDK's single-round-trip subscribe applies;
              // fall back to name inference if the metadata is absent.
              const metaKinds = trackEntries.map(([, t]) => t?.kind)
              const kinds = metaKinds.every((k) => k === 'audio' || k === 'video')
                ? (metaKinds as ('audio' | 'video')[])
                : kindsForTrackNames(names)
              candidates.push({ participant_id: pid, track_names: names, track_kinds: kinds })
            }
            if (candidates.length === 0) return
            console.info(`[useCall] sweep "${label}" subscribing to ${candidates.length} peers:`, candidates.map((s) => s.participant_id))
            // Use the watchdog-wrapped helper so each peer is subscribed
            // individually + monitored for actual track arrival. The
            // watchdog will force re-subscribe if /subscribe returns
            // 200 but no ontrack lands within 8s. Mark peers as swept
            // only after their individual subscribe call returns OK
            // (handled inside subscribeWithWatchdog's success path).
            for (const c of candidates) {
              // Pre-check: subscribeWithWatchdog awaits per peer; mark
              // each peer as swept once their individual subscribe
              // resolves. On failure the helper already evicts.
              await subscribeWithWatchdog([c], `sweep-${label}`)
              if (peer.getConnectionState() !== 'closed') {
                // Helper only swallows errors — mark swept on any
                // outcome where the peer remains in subscribedPeerIds.
                // The watchdog handles real failures via eviction.
                sweptPeerIdsRef.current.add(c.participant_id)
              }
            }
          } catch (e) {
            console.warn(`[useCall] sweep "${label}" failed`, e)
          }
        }
        sweepRef.current = runSweep
        await runSweep('initial')
        // Second sweep after 1.5s — covers peers whose tracks were not
        // yet visible in KZ when the first sweep ran.
        window.setTimeout(() => runSweep('delayed'), 1500)
        // Periodic background sweep every 15s. Self-heals any drift —
        // missed cable events, transient KZ propagation delays, partial
        // SDP exchanges — without waiting for a cable reconnect. Each
        // sweep is O(participant_count) and cheap because subscribed
        // peers short-circuit at the SDK's `subscribedPeerIds` check.
        const sweepInterval = window.setInterval(() => {
          if (cancelled) return
          void runSweep('periodic')
        }, 15_000)
        const priorCleanup = cleanupRef.current
        cleanupRef.current = () => {
          priorCleanup?.()
          window.clearInterval(sweepInterval)
        }
      } catch (e) {
        if (cancelled) return
        const msg = e instanceof Error ? e.message : 'unknown'
        setError(msg)
        setState('failed')
      }
    })()

    // Tab close / iOS Safari background → fire a best-effort leave so the
    // server-side participant row gets `left_at` and the KonexZero proxy
    // gets a /leave POST. Without this, participants linger until KZ's
    // idle eviction (~30–90s with no liveness signal) before peers see
    // them drop. We use
    // `pagehide` because it fires reliably across browsers including iOS
    // Safari, where `beforeunload` is unreliable.
    const onPageHide = () => {
      if (!roomKey) return
      const csrfNow = (document.querySelector('meta[name=csrf-token]') as HTMLMetaElement | null)?.content ?? ''
      try {
        fetch(`/calls/${roomKey}/leave`, {
          method: 'POST',
          headers: { 'X-CSRF-Token': csrfNow, 'X-Requested-With': 'XMLHttpRequest' },
          credentials: 'same-origin',
          keepalive: true
        }).catch(() => {})
      } catch { /* ignore */ }
      try {
        fetch(`${PROXY_BASE}/rooms/${roomKey}/leave`, {
          method: 'POST',
          headers: {
            'X-CSRF-Token': csrfNow,
            'X-Requested-With': 'XMLHttpRequest',
            'X-Participant-Id': `user_${opts.myUserId}`
          },
          credentials: 'same-origin',
          keepalive: true
        }).catch(() => {})
      } catch { /* ignore */ }
    }
    window.addEventListener('pagehide', onPageHide)

    return () => {
      cancelled = true
      window.removeEventListener('pagehide', onPageHide)
      cleanupRef.current?.()
      cleanupRef.current = null
      sweepRef.current = null
      sweptPeerIdsRef.current = new Set()
      publishedTrackNamesRef.current = []
      if (!roomKey) return
      const csrf = (document.querySelector('meta[name=csrf-token]') as HTMLMetaElement | null)?.content ?? ''
      // Best-effort tell the Rails side we left
      fetch(`/calls/${roomKey}/leave`, {
        method: 'POST',
        headers: { 'X-CSRF-Token': csrf, 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'same-origin',
        keepalive: true
      }).catch(() => {})
      peerRef.current?.leave().catch(() => {})
      channelRef.current?.unsubscribe()
      // Intentional leave invalidates the upstream session — drop the cached
      // id so the next mount doesn't try to resume a dead session.
      if (typeof window !== 'undefined') {
        window.sessionStorage.removeItem(sessionStorageKey)
      }
    }
  }, [roomKey])

  return {
    state,
    error,
    mediaError,
    connectionState,
    localStream,
    remotePeers,
    micEnabled,
    camEnabled,
    toggleMic: () => {
      const next = !micEnabled
      peerRef.current?.setLocalTrackEnabled('audio', next)
      setMicEnabled(next)
      channelRef.current?.perform('media_toggle', { kind: 'audio', enabled: next })
    },
    toggleCam: () => {
      const next = !camEnabled
      peerRef.current?.setLocalTrackEnabled('video', next)
      setCamEnabled(next)
      channelRef.current?.perform('media_toggle', { kind: 'video', enabled: next })
    },
    end: async () => {
      if (!roomKey || !opts) return
      const csrf = (document.querySelector('meta[name=csrf-token]') as HTMLMetaElement | null)?.content ?? ''
      if (opts.isHost) {
        await fetch(`/calls/${roomKey}/end`, {
          method: 'POST',
          headers: { 'X-CSRF-Token': csrf, 'X-Requested-With': 'XMLHttpRequest' },
          credentials: 'same-origin'
        })
      }
      await peerRef.current?.leave()
    },
    perform: (action, data) => channelRef.current?.perform(action, data)
  }
}
